import type { GoalWithSubtasks, SubtaskWithResult } from './types';
import { aiWorkDispatches } from './ai-work-kind';
import type { WorkItem } from './work-list-view';

// #2749: the words of the detail beside the list (「ご確認いただきたい事項」),
// the same as the extension's task detail (#2748 board 6).

type Lang = 'ja' | 'en';

/** What the detail opens: one task, a goal, or a goal candidate (or a held thread message). */
export type WorkDetailTarget =
  /** `fromGoal`: opened from its goal's detail, so closing goes back there. */
  | { type: 'task'; goalId: string; subtaskId: string; key: string | null; fromGoal?: boolean }
  | { type: 'goal'; goalId: string; key: string | null }
  | { type: 'candidate'; key: string };

export type TaskDetailState = 'invalidated' | 'done' | 'result' | 'no-result' | 'dispatch' | 'running' | 'ai' | 'yours';

/** A prepared-action task: the runtime owns it, so the Web does not edit it. */
export function isRuntimeTask(st: Pick<SubtaskWithResult, 'metadata'>): boolean {
  const compat = st.metadata?.action_runtime_compat as Record<string, unknown> | undefined;
  return !!compat && typeof compat === 'object' && compat.schema_version === 'prepared_action_compat_subtask.v1';
}

/** What the screen is about for this task now. */
export function taskDetailState(st: Pick<SubtaskWithResult, 'status' | 'executor' | 'ai_result' | 'ai_action' | 'metadata'>): TaskDetailState {
  if (st.status === 'invalidated') return 'invalidated';
  if (st.status === 'done') return 'done';
  const result = st.ai_result;
  if (st.status === 'review') {
    if (result == null) return 'no-result';
    if (result.approved_at && !result.dispatched_at && aiWorkDispatches(st)) return 'dispatch';
    return 'result';
  }
  if (st.status === 'in_progress') return 'running';
  return st.executor === 'ai' ? 'ai' : 'yours';
}

/** The secretary's one line on what this task needs from the user. */
export function taskDetailLead(state: TaskDetailState, redo: boolean, lang: Lang, blocked = false): string {
  const en = lang === 'en';
  switch (state) {
    case 'invalidated': return en ? 'This task no longer needs doing.' : 'このタスクは、もう進める必要がありません。';
    case 'done': return en ? 'This task is done.' : 'このタスクは完了しています。';
    case 'result': return en ? 'The AI has prepared this. Please check it.' : 'AIが用意した内容です。ご確認ください。';
    case 'no-result': return en ? "The AI's result has not arrived yet." : 'AIの結果がまだ届いていません。';
    case 'dispatch': return en ? 'Approved. The AI will carry it out.' : 'ご承認いただきました。AIが実行します。';
    case 'running': return en ? 'The AI is working on this.' : 'AIが進めています。';
    case 'ai':
      return redo
        ? (en ? 'The AI will redo this with your notes.' : 'お伝えいただいた点を直して、AIがやり直します。')
        : blocked
          // Waits for the tasks before it (status 'blocked'), so not "next".
          ? (en ? 'The AI will work on this once the tasks before it are done.' : '前のタスクが済んだら、AIが進めます。')
          : (en ? 'The AI will work on this next.' : 'AIがこれから進めます。');
    default: return en ? 'This one is for you.' : 'あなたにお願いしたいタスクです。';
  }
}

/** The task's own completion conditions, without the one that only repeats its title. */
export function taskConditions(st: Pick<SubtaskWithResult, 'dod_items' | 'title'>): Array<{ text: string; done: boolean; index: number }> {
  const plain = (value: unknown) => String(value || '').replace(/[\s。．.]/g, '');
  return (st.dod_items || [])
    .map((item, index) => ({ text: item?.text ?? '', done: item?.done === true, index }))
    .filter((item) => plain(item.text) && plain(item.text) !== `${plain(st.title)}が完了している`);
}

/** The goal's source as a link, from source_url or its first source ref. */
export function goalSourceLink(goal: Pick<GoalWithSubtasks, 'source_url' | 'source_refs' | 'source_type' | 'created_from'>): { url: string | null; kind: string | null } {
  const ref = Array.isArray(goal.source_refs) ? goal.source_refs.find((r) => r && (r.source_ref || r.source_type)) : null;
  return {
    url: goal.source_url || ref?.source_ref || null,
    kind: goal.source_type || ref?.source_type || goal.created_from || null,
  };
}

/**
 * Which detail a list row opens: its task when it names one the goal still
 * has, the candidate when it is one, and otherwise its goal.
 */
export function detailTargetFor(item: WorkItem, goal: Pick<GoalWithSubtasks, 'subtasks' | 'decisions'> | null): WorkDetailTarget {
  if (item.candidate_id || item.goal_link) return { type: 'candidate', key: item.key };
  const subtaskId = item.subtask_id;
  if (subtaskId && goal && [...(goal.subtasks || []), ...(goal.decisions || [])].some((st) => st.id === subtaskId)) {
    return { type: 'task', goalId: item.goal_id, subtaskId, key: item.key };
  }
  return { type: 'goal', goalId: item.goal_id, key: item.key };
}

/** The calendar day of a moment in the user's time zone, as YYYY-MM-DD. */
export function dayIn(at: Date, timezone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(at);
  } catch {
    return at.toISOString().slice(0, 10);
  }
}

/** 「今日 N 件目の完了です」, counted from the goals done today in the user's time zone. */
export function todayDoneLine(
  goals: Array<{ id: string; done_at?: string | null }>,
  goalId: string,
  hasMore: boolean,
  lang: Lang,
  timezone: string,
  now: Date = new Date(),
): string {
  const today = dayIn(now, timezone);
  const doneToday = goals.filter((goal) => goal.done_at && dayIn(new Date(goal.done_at), timezone) === today);
  // The PATCH may not have landed yet; this completion still counts.
  const count = doneToday.length + (doneToday.some((goal) => goal.id === goalId) ? 0 : 1);
  // The page is newest first: when all of it is today, older ones may be too.
  const more = hasMore && doneToday.length === goals.length;
  if (lang === 'en') return more ? `${count}+ goals completed today` : `Goal #${count} completed today`;
  return more ? `今日 ${count} 件以上を完了しています` : `今日 ${count} 件目の完了です`;
}
